import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import extraClassesService from '../../services/extraClassesService';
import staffService from '../../services/staffService';
import SubmissionGradingPanel from './SubmissionGradingPanel';
import DialogModal from '../../components/DialogModal';
import { CheckCircle, X, ClipboardText, Upload, Plus, Trash, ChalkboardTeacher, CurrencyCircleDollar, Clock, UsersThree, DownloadSimple } from '@phosphor-icons/react';
import './TeacherExtraClassesSection.css';

function TeacherExtraClassesSection({ taughtClasses }) {
  const [classes, setClasses] = useState([]);
  const [form, setForm] = useState({
    title: '',
    description: '',
    subject_id: '',
    class_id: '',
    pricing_type: 'hourly',
    price: '',
    session_duration_hours: 1,
    frequency_per_week: 1,
    max_students_per_session: 10,
    contact_phone: '',
    contact_email: '',
    meeting_link: '',
    schedule: '',
    start_date: '',
    end_date: '',
    status: 'draft',
    billing_interval: 'monthly',
    payout_frequency: 'monthly',
  });
  const [loading, setLoading] = useState(true);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedAssignmentId, setSelectedAssignmentId] = useState('');
  const [earnings, setEarnings] = useState({ total_earned: 0, cycle_count: 0, cycles: [] });
  const [publishingId, setPublishingId] = useState(null);
  const [payoutDetails, setPayoutDetails] = useState(null);
  const [payoutForm, setPayoutForm] = useState({ account_type: 'mobile_money', bank_code: '', account_number: '' });
  const [banks, setBanks] = useState([]);
  const [submittingPayoutDetails, setSubmittingPayoutDetails] = useState(false);
  const [reminderDrafts, setReminderDrafts] = useState({});
  const [assignmentForm, setAssignmentForm] = useState({ title: '', description: '', instructions: '', due_date: '', max_score: 100 });
  const [questions, setQuestions] = useState([]);
  const [questionForm, setQuestionForm] = useState({ question: '', type: 'essay', options: '', answer: '', points: 1 });
  const [questionFile, setQuestionFile] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [showGradingPanel, setShowGradingPanel] = useState(false);
  const [gradingSubmissionIndex, setGradingSubmissionIndex] = useState(0);
  const [assignmentsByClass, setAssignmentsByClass] = useState({});
  const [enrollmentRequestsByClass, setEnrollmentRequestsByClass] = useState({});
  const [assignmentSummary, setAssignmentSummary] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState('classes');
  const [editingAssignmentId, setEditingAssignmentId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionForm, setSessionForm] = useState({ session_date: '', topic: '', notes: '', attendance_count: 0 });
  const [submittingSession, setSubmittingSession] = useState(false);
  const [sendingSessionReminder, setSendingSessionReminder] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, type, confirmLabel, onConfirm }

  const csvExampleTemplate = `question,type,options,answer,points
What is the main idea of the passage?,essay,,The main idea is...,5
What is 2 + 2?,multipleChoice,3|4|5|6,4,2
Define the term metabolism,shortAnswer,,The sum of all chemical processes in living organisms.,3
Match the country to its capital,matching,Egypt=Cairo|Nigeria=Lagos|Ghana=Accra,Egypt=Cairo|Nigeria=Lagos|Ghana=Accra,4
Which planet is known as the Red Planet?,multipleChoice,Venus|Mars|Jupiter|Saturn,Mars,2
What is the boiling point of water at sea level?,shortAnswer,,100°C,2
Who painted the Mona Lisa?,shortAnswer,,Leonardo da Vinci,2
What is the formula for the area of a rectangle?,shortAnswer,,Length × width,2
Match the animal to its habitat,matching,Polar bear=Arctic|Camel=Desert|Frog=Wetland|Eagle=Mountain,Polar bear=Arctic|Camel=Desert|Frog=Wetland|Eagle=Mountain,4
What does the acronym CPU stand for?,shortAnswer,,Central Processing Unit,2
Which gas do plants absorb from the atmosphere?,multipleChoice,Oxygen|Carbon dioxide|Nitrogen|Hydrogen,Carbon dioxide,2
Describe globalization in one sentence.,essay,,The increasing interconnectedness of countries through trade, communication, and culture.,3`;

  const handleDownloadCSVTemplate = () => {
    const element = document.createElement('a');
    const file = new Blob([csvExampleTemplate], { type: 'text/csv' });
    element.href = URL.createObjectURL(file);
    element.download = 'extra_class_question_template.csv';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    toast.success('CSV template downloaded successfully');
  };

  const loadAssignments = async (extraClassId) => {
    if (!extraClassId) return;
    try {
      const data = await extraClassesService.listAssignments(extraClassId);
      setAssignmentsByClass((prev) => ({ ...prev, [extraClassId]: data.assignments || [] }));
    } catch (err) {
      toast.error(err.message || 'Unable to load assignments');
    }
  };

  const loadEnrollmentRequests = async (extraClassId) => {
    if (!extraClassId) return;
    try {
      const data = await extraClassesService.listEnrollments(extraClassId);
      setEnrollmentRequestsByClass((prev) => ({ ...prev, [extraClassId]: data.enrollments || [] }));
    } catch (err) {
      console.error('Unable to load enrollment requests', err);
    }
  };

  const loadClasses = async () => {
    try {
      const data = await extraClassesService.getTeacherClasses();
      const loadedClasses = data.extra_classes || [];
      setClasses(loadedClasses);
      await Promise.all(loadedClasses.map((item) => Promise.all([loadAssignments(item.id), loadEnrollmentRequests(item.id)])));
      if (loadedClasses.length > 0 && !selectedClassId) {
        setSelectedClassId(loadedClasses[0].id);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load extra classes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadClasses(); }, []);

  useEffect(() => {
    if (!selectedClassId && classes.length > 0) {
      setSelectedClassId(classes[0].id);
    }
  }, [classes, selectedClassId]);

  useEffect(() => {
    if (!selectedClassId) {
      setEarnings({ total_earned: 0, cycle_count: 0, cycles: [] });
      return;
    }
    extraClassesService.getClassEarnings(selectedClassId)
      .then((data) => setEarnings(data))
      .catch(() => setEarnings({ total_earned: 0, cycle_count: 0, cycles: [] }));
  }, [selectedClassId]);

  const loadSessions = async (extraClassId) => {
    if (!extraClassId) {
      setSessions([]);
      return;
    }
    try {
      const data = await extraClassesService.listSessions(extraClassId);
      setSessions(data.sessions || []);
    } catch (err) {
      setSessions([]);
    }
  };

  useEffect(() => {
    if (activeSubTab === 'sessions') {
      loadSessions(selectedClassId);
    }
  }, [activeSubTab, selectedClassId]);

  const handleLogSession = async (e) => {
    e.preventDefault();
    if (!selectedClassId) {
      toast.error('Please select a class first');
      return;
    }
    if (!sessionForm.session_date) {
      toast.error('Please pick a session date and time');
      return;
    }
    try {
      setSubmittingSession(true);
      await extraClassesService.createSession(selectedClassId, {
        session_date: sessionForm.session_date,
        topic: sessionForm.topic.trim() || undefined,
        notes: sessionForm.notes.trim() || undefined,
        attendance_count: Number(sessionForm.attendance_count || 0),
      });
      toast.success('Session logged');
      setSessionForm({ session_date: '', topic: '', notes: '', attendance_count: 0 });
      await loadSessions(selectedClassId);
    } catch (err) {
      toast.error(err.message || 'Unable to log session');
    } finally {
      setSubmittingSession(false);
    }
  };

  const handleSendSessionReminder = async () => {
    if (!selectedClassId) {
      toast.error('Please select a class first');
      return;
    }
    const selectedClass = classes.find((item) => item.id === selectedClassId);
    try {
      setSendingSessionReminder(true);
      await extraClassesService.remindSession(selectedClassId);
      toast.success(`Session reminder sent to parents of ${selectedClass?.title || 'this class'}`);
    } catch (err) {
      toast.error(err.message || 'Unable to send session reminder');
    } finally {
      setSendingSessionReminder(false);
    }
  };

  const loadPayoutDetails = () => {
    staffService.getMyPayoutDetails()
      .then((data) => {
        setPayoutDetails(data);
        if (data.payout_account_type) {
          setPayoutForm({ account_type: data.payout_account_type, bank_code: data.payout_bank_code || '', account_number: data.payout_account_number || '' });
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    loadPayoutDetails();
    staffService.listPayoutBanks().then((data) => setBanks(data.banks || [])).catch(() => setBanks([]));
  }, []);

  const handleSubmitPayoutDetails = async (e) => {
    e.preventDefault();
    if (!payoutForm.bank_code || !payoutForm.account_number) {
      toast.error('Select a bank/provider and enter your account number');
      return;
    }
    try {
      setSubmittingPayoutDetails(true);
      const result = await staffService.submitMyPayoutDetails(payoutForm);
      setPayoutDetails(result);
      toast.success(`Verified as ${result.payout_account_name} — submitted for admin verification`);
    } catch (err) {
      toast.error(err.message || 'Unable to submit payout details');
    } finally {
      setSubmittingPayoutDetails(false);
    }
  };

  const handlePublish = async (extraClassId) => {
    try {
      setPublishingId(extraClassId);
      await extraClassesService.updateClass(extraClassId, { status: 'published' });
      toast.success('Class published — parents can now see and register for it');
      loadClasses();
    } catch (err) {
      toast.error(err.message || 'Unable to publish class');
    } finally {
      setPublishingId(null);
    }
  };

  const handleSubmissionsClassChange = async (classId) => {
    setSelectedClassId(classId);
    setSelectedAssignmentId('');
    setSubmissions([]);
    if (classId) {
      await loadAssignments(classId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await extraClassesService.createClass({
        ...form,
        price: Number(form.price),
        session_duration_hours: Number(form.session_duration_hours),
        frequency_per_week: Number(form.frequency_per_week),
        max_students_per_session: Number(form.max_students_per_session),
        // Optional date inputs come back as '' when left blank, which the
        // backend's datetime parser rejects outright (distinct from simply
        // omitting the field). Convert blank to undefined so JSON.stringify
        // drops the key instead of sending an unparseable empty string.
        start_date: form.start_date || undefined,
        end_date: form.end_date || undefined,
      });
      toast.success('Extra class created');
      setForm({ ...form, title: '', description: '', price: '', contact_phone: '', contact_email: '', meeting_link: '', schedule: '', start_date: '', end_date: '' });
      loadClasses();
    } catch (err) {
      toast.error(err.message || 'Failed to create extra class');
    }
  };

  const loadSubmissions = async (extraClassId, assignmentId) => {
    if (!extraClassId || !assignmentId) {
      setSubmissions([]);
      return;
    }

    try {
      const data = await extraClassesService.getSubmissions(extraClassId, assignmentId);
      setSubmissions(data.submissions || []);
    } catch (err) {
      toast.error(err.message || 'Unable to load submissions');
    }
  };

  const checkReminderEligibility = async (extraClassId) => {
    try {
      const data = await extraClassesService.listEnrollments(extraClassId);
      return (data.enrollments || []).length > 0;
    } catch (err) {
      console.error('Reminder eligibility check failed', err);
      return false;
    }
  };

  const handleReminderSend = async (e, extraClassId, classTitle) => {
    e.preventDefault();
    if (!extraClassId) {
      toast.error('Please select a class first');
      return;
    }

    const hasRegistrations = await checkReminderEligibility(extraClassId);
    if (!hasRegistrations) {
      toast.warning('There must be at least one child registered for this class before sending a reminder.');
      return;
    }

    try {
      await extraClassesService.sendParentReminder(
        extraClassId,
        reminderDrafts[extraClassId] || `Reminder: your payment for ${classTitle || 'this extra class'} is due.`
      );
      toast.success(`Reminder sent for ${classTitle || 'this class'}`);
      setReminderDrafts((prev) => ({ ...prev, [extraClassId]: '' }));
    } catch (err) {
      toast.error(err.message || 'Unable to send reminder');
    }
  };

  const handleEnrollmentAction = async (extraClassId, enrollmentId, action) => {
    try {
      await extraClassesService.actOnEnrollment(extraClassId, enrollmentId, action);
      toast.success(`Enrollment ${action === 'approve' ? 'approved' : 'rejected'}`);
      await loadEnrollmentRequests(extraClassId);
    } catch (err) {
      toast.error(err.message || `Unable to ${action} enrollment`);
    }
  };

  const normalizeQuestionPayload = (question) => {
    const prompt = String(question?.question || '').trim();
    if (!prompt) return null;

    const rawOptions = question?.options;
    let normalizedOptions = [];
    if (Array.isArray(rawOptions)) {
      normalizedOptions = rawOptions.map((item) => String(item || '').trim()).filter(Boolean);
    } else if (typeof rawOptions === 'string') {
      normalizedOptions = rawOptions.split('|').map((item) => item.trim()).filter(Boolean);
    }

    return {
      question: prompt,
      question_text: prompt,
      type: question?.type || 'essay',
      question_type: question?.type || 'essay',
      options: normalizedOptions,
      answer: question?.answer ?? '',
      correct_answer: question?.answer ?? '',
      points: Number(question?.points || 1),
    };
  };

  const handleAssignmentCreate = async (e) => {
    e.preventDefault();
    if (!selectedClassId) {
      toast.error('Please select a class first');
      return;
    }

    const trimmedTitle = String(assignmentForm.title || '').trim();
    if (!trimmedTitle) {
      toast.error('Please enter an assignment title');
      return;
    }

    const normalizedQuestions = questions
      .map(normalizeQuestionPayload)
      .filter(Boolean);

    if (normalizedQuestions.length === 0) {
      toast.error('Please add at least one question before saving');
      return;
    }

    const payload = {
      ...assignmentForm,
      title: trimmedTitle,
      description: String(assignmentForm.description || '').trim(),
      instructions: String(assignmentForm.instructions || '').trim(),
      max_score: Number(assignmentForm.max_score || 100),
      questions: normalizedQuestions,
    };

    try {
      if (editingAssignmentId) {
        await extraClassesService.updateAssignment(selectedClassId, editingAssignmentId, payload);
        toast.success('Assignment updated');
        setAssignmentSummary(null);
      } else {
        const response = await extraClassesService.createAssignment(selectedClassId, payload);
        const selectedClass = classes.find((item) => item.id === selectedClassId);
        setAssignmentSummary({
          id: response?.assignment?.id || null,
          classId: selectedClassId,
          title: trimmedTitle,
          classTitle: selectedClass?.title || 'Selected class',
          dueDate: assignmentForm.due_date || 'No due date',
          maxScore: Number(assignmentForm.max_score || 100),
          questionsCount: normalizedQuestions.length,
          description: String(assignmentForm.description || '').trim() || 'No description provided',
        });
        toast.success('Assignment created');
      }
      setEditingAssignmentId(null);
      setAssignmentForm({ title: '', description: '', instructions: '', due_date: '', max_score: 100 });
      setQuestions([]);
      setQuestionForm({ question: '', type: 'essay', options: '', answer: '', points: 1 });
      setQuestionFile(null);
      await loadAssignments(selectedClassId);
    } catch (err) {
      console.error('Assignment save error', err);
      toast.error(err.message || 'Unable to save assignment');
    }
  };

  const handleEditAssignment = async (assignment) => {
    try {
      const data = await extraClassesService.getAssignment(selectedClassId, assignment.id);
      const full = data.assignment || data;
      setAssignmentForm({
        title: full.title || '',
        description: full.description || '',
        instructions: full.instructions || '',
        due_date: full.due_date ? full.due_date.slice(0, 10) : '',
        max_score: full.max_score || 100,
      });
      const loadedQuestions = (full.questions || []).map((q) => {
        if (q.type === 'matching' && Array.isArray(q.items)) {
          const pairs = (q.options || []).map((left, idx) => `${left}=${q.items[idx] || ''}`);
          return { question: q.question, type: q.type, options: pairs, answer: q.answer || '', points: q.points };
        }
        return { question: q.question, type: q.type, options: q.options || [], answer: q.answer || '', points: q.points };
      });
      setQuestions(loadedQuestions);
      setEditingAssignmentId(assignment.id);
      setAssignmentSummary(null);
    } catch (err) {
      toast.error(err.message || 'Unable to load assignment for editing');
    }
  };

  const handleCancelEditAssignment = () => {
    setEditingAssignmentId(null);
    setAssignmentForm({ title: '', description: '', instructions: '', due_date: '', max_score: 100 });
    setQuestions([]);
    setQuestionForm({ question: '', type: 'essay', options: '', answer: '', points: 1 });
    setQuestionFile(null);
  };

  const handleDeleteAssignment = (assignment) => {
    setConfirmDialog({
      title: 'Delete Assignment',
      message: `Delete "${assignment.title}"? This also removes its submissions and grades.`,
      type: 'error',
      confirmLabel: 'Delete',
      onConfirm: async () => {
        try {
          await extraClassesService.deleteAssignment(selectedClassId, assignment.id);
          toast.success('Assignment deleted');
          if (editingAssignmentId === assignment.id) handleCancelEditAssignment();
          await loadAssignments(selectedClassId);
        } catch (err) {
          toast.error(err.message || 'Unable to delete assignment');
        }
      },
    });
  };

  const openSubmissionReview = (submission) => {
    const index = submissions.findIndex((item) => item.id === submission.id);
    setGradingSubmissionIndex(index >= 0 ? index : 0);
    setShowGradingPanel(true);
  };

  const handleViewSolvedStudents = async (extraClassId, assignmentId) => {
    if (!extraClassId || !assignmentId) return;

    setSelectedClassId(extraClassId);
    setSelectedAssignmentId(assignmentId);
    setActiveSubTab('submissions');
    await loadSubmissions(extraClassId, assignmentId);
  };

  const handleAddQuestion = () => {
    if (!questionForm.question) {
      toast.error('Please enter the question prompt');
      return;
    }

    setQuestions((prev) => [
      ...prev,
      {
        ...questionForm,
        options: questionForm.options
          ? questionForm.options.split('|').map((item) => item.trim()).filter(Boolean)
          : [],
      },
    ]);

    setQuestionForm({ question: '', type: 'essay', options: '', answer: '', points: 1 });
  };

  const handleRemoveQuestion = (index) => {
    setQuestions((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleCSVUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setQuestionFile(file);

    const reader = new FileReader();
    reader.onload = (e) => {
      const csv = e.target.result;
      const lines = csv.split(/\r?\n/).filter((line) => line.trim());
      if (lines.length < 2) {
        toast.error('CSV must have a header row and at least one question');
        return;
      }

      const headers = lines[0].split(',').map((header) => header.trim().toLowerCase());
      const questionIndex = headers.indexOf('question');
      const typeIndex = headers.indexOf('type');
      const optionsIndex = headers.indexOf('options');
      const answerIndex = headers.indexOf('answer');
      const pointsIndex = headers.indexOf('points');

      if (questionIndex === -1) {
        toast.error('CSV must have a question column');
        return;
      }

      const parsed = [];
      for (let i = 1; i < lines.length; i += 1) {
        const values = lines[i].split(',');
        if (!values[questionIndex]?.trim()) continue;
        parsed.push({
          question: values[questionIndex].trim(),
          type: values[typeIndex]?.trim() || 'essay',
          options: values[optionsIndex]?.trim()
            ? values[optionsIndex].split('|').map((item) => item.trim()).filter(Boolean)
            : [],
          answer: values[answerIndex]?.trim() || '',
          points: Number(values[pointsIndex]?.trim() || 1),
        });
      }

      if (parsed.length === 0) {
        toast.error('No valid questions were found in the CSV');
        return;
      }

      setQuestions((prev) => [...prev, ...parsed]);
      toast.success(`Loaded ${parsed.length} questions from CSV`);
    };
    reader.readAsText(file);
  };


  const closeSubmissionReview = () => {
    setShowGradingPanel(false);
    setGradingSubmissionIndex(0);
  };

  const formatStatusBadge = (status) => {
    const normalized = String(status || '').toLowerCase();
    if (normalized === 'graded') return { text: 'Graded', color: '#10b981', background: '#ecfdf5' };
    if (normalized === 'late') return { text: 'Late', color: '#dc2626', background: '#fef2f2' };
    if (normalized === 'submitted') return { text: 'Submitted', color: '#3b82f6', background: '#eff6ff' };
    if (normalized === 'pending') return { text: 'Pending', color: '#f59e0b', background: '#ffedd5' };
    return { text: String(status || 'Unknown'), color: '#6b7280', background: '#f3f4f6' };
  };

  const isJsonAnswers = (text) => {
    if (!text) return false;
    try {
      const parsed = JSON.parse(text);
      // Consider it answers JSON when it's an object or has an `answers` key
      return typeof parsed === 'object' && (parsed.answers || Object.keys(parsed).length > 0);
    } catch (e) {
      return false;
    }
  };


  return (
    <div className="extra-classes-section">
      <div className="extra-classes-hero">
        <div className="hero-copy">
          <div className="hero-badge">
            <ChalkboardTeacher size={16} />
            Extra classes workspace
          </div>
          <h2>Create, manage, and review your tutoring sessions in one place.</h2>
          <p>Set up classes, publish assignments, track submissions, and follow up on payments without leaving the portal.</p>
        </div>
        <div className="hero-stats">
          <div className="hero-stat">
            <strong>{classes.length}</strong>
            <span>Classes</span>
          </div>
          <div className="hero-stat">
            <strong>{Object.keys(assignmentsByClass).length}</strong>
            <span>Assignments</span>
          </div>
          <div className="hero-stat">
            <strong>{submissions.length}</strong>
            <span>Submissions</span>
          </div>
        </div>
      </div>

      <div className="subtab-nav" role="tablist" aria-label="Extra classes workspace sections">
        <button type="button" className={`subtab-btn ${activeSubTab === 'classes' ? 'active' : ''}`} onClick={() => setActiveSubTab('classes')}>
          <span className="subtab-icon"><ChalkboardTeacher size={18} /></span>
          <span className="subtab-copy">
            <span className="subtab-label">Classes</span>
            <span className="subtab-description">Create and manage sessions</span>
          </span>
        </button>
        <button type="button" className={`subtab-btn ${activeSubTab === 'assignments' ? 'active' : ''}`} onClick={() => setActiveSubTab('assignments')}>
          <span className="subtab-icon"><ClipboardText size={18} /></span>
          <span className="subtab-copy">
            <span className="subtab-label">Assignments</span>
            <span className="subtab-description">Publish work and review questions</span>
          </span>
        </button>
        <button type="button" className={`subtab-btn ${activeSubTab === 'payouts' ? 'active' : ''}`} onClick={() => setActiveSubTab('payouts')}>
          <span className="subtab-icon"><CurrencyCircleDollar size={18} /></span>
          <span className="subtab-copy">
            <span className="subtab-label">Payouts</span>
            <span className="subtab-description">Track requests and earnings</span>
          </span>
        </button>
        <button type="button" className={`subtab-btn ${activeSubTab === 'submissions' ? 'active' : ''}`} onClick={() => setActiveSubTab('submissions')}>
          <span className="subtab-icon"><UsersThree size={18} /></span>
          <span className="subtab-copy">
            <span className="subtab-label">Submissions</span>
            <span className="subtab-description">Grade work from enrolled learners</span>
          </span>
        </button>
        <button type="button" className={`subtab-btn ${activeSubTab === 'sessions' ? 'active' : ''}`} onClick={() => setActiveSubTab('sessions')}>
          <span className="subtab-icon"><Clock size={18} /></span>
          <span className="subtab-copy">
            <span className="subtab-label">Sessions</span>
            <span className="subtab-description">Log attendance parents can see</span>
          </span>
        </button>
      </div>

      {activeSubTab === 'classes' && (
        <>
          <div className="section-card">
            <div className="section-header">
              <div>
                <h3>Create an extra class</h3>
                <p>Make a new support lesson for students who need more guidance.</p>
              </div>
            </div>
            <form onSubmit={handleSubmit} className="section-form">
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Class title" required />
              <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Short description" />
              <select value={form.subject_id} onChange={(e) => setForm({ ...form, subject_id: e.target.value })} required>
                <option value="">Select subject</option>
                {taughtClasses?.map((item) => (
                  <option key={item.subject_id || item.id} value={item.subject_id || item.id}>{item.subject_name || item.name}</option>
                ))}
              </select>
              <select value={form.class_id} onChange={(e) => setForm({ ...form, class_id: e.target.value })}>
                <option value="">Select class (optional)</option>
                {taughtClasses?.map((item) => (
                  <option key={item.class_id || item.id} value={item.class_id || item.id}>{item.class_name || item.name}</option>
                ))}
              </select>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <select value={form.pricing_type} onChange={(e) => setForm({ ...form, pricing_type: e.target.value })}>
                  <option value="hourly">Hourly</option>
                  <option value="monthly">Monthly</option>
                </select>
                <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Price" type="number" required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <select value={form.billing_interval} onChange={(e) => setForm({ ...form, billing_interval: e.target.value })}>
                  <option value="monthly">Monthly billing</option>
                  <option value="weekly">Weekly billing</option>
                  <option value="one_time">One-time billing</option>
                </select>
                <select value={form.payout_frequency} onChange={(e) => setForm({ ...form, payout_frequency: e.target.value })}>
                  <option value="monthly">Monthly payout</option>
                  <option value="weekly">Weekly payout</option>
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <input value={form.session_duration_hours} onChange={(e) => setForm({ ...form, session_duration_hours: e.target.value })} placeholder="Duration in hours" type="number" />
                <input value={form.frequency_per_week} onChange={(e) => setForm({ ...form, frequency_per_week: e.target.value })} placeholder="Times per week" type="number" />
              </div>
              <input value={form.max_students_per_session} onChange={(e) => setForm({ ...form, max_students_per_session: e.target.value })} placeholder="Max students" type="number" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="Contact phone" />
                <input value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} placeholder="Contact email" />
              </div>
              <input value={form.meeting_link} onChange={(e) => setForm({ ...form, meeting_link: e.target.value })} placeholder="Meeting link" />
              <input value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} placeholder="Schedule" />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <input value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} type="date" />
                <input value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} type="date" />
              </div>
              <button className="button-primary" type="submit">Save class</button>
            </form>
          </div>

          <div className="section-card">
            <div className="section-header">
              <div>
                <h3>Your extra classes</h3>
                <p>Keep an eye on each support lesson and its setup details.</p>
              </div>
            </div>
            {loading ? <div className="empty-state"><p>Loading your classes…</p></div> : classes.length === 0 ? (
              <div className="empty-state">
                <ChalkboardTeacher size={28} />
                <p>No classes yet. Create your first one to get started.</p>
              </div>
            ) : (
              <div className="class-list">
                {classes.map((item) => (
                  <div key={item.id} className="class-card">
                    <div className="class-card-header">
                      <div>
                        <h4 className="class-title">{item.title}</h4>
                        <p className="class-subtitle">Extra support lesson for students who need more help beyond regular teaching.</p>
                      </div>
                      <span className="status-pill">{(item.status || 'draft').replace('_', ' ')}</span>
                    </div>
                    <div className="class-meta">
                      <div><strong>Price:</strong> GHS {item.price} ({item.pricing_type})</div>
                      <div><strong>Billing:</strong> {item.billing_interval || 'monthly'}</div>
                      <div><strong>Payout:</strong> {item.payout_frequency || 'monthly'}</div>
                      <div><strong>Capacity:</strong> {item.enrolled_count ?? 0}/{item.max_students_per_session} students enrolled</div>
                      <div><strong>Contact:</strong> {item.contact_phone || item.contact_email || 'Not provided'}</div>
                    </div>
                    {item.status === 'draft' && (
                      <div style={{ margin: '4px 0 12px' }}>
                        <button
                          type="button"
                          className="button-primary small"
                          disabled={publishingId === item.id}
                          onClick={() => handlePublish(item.id)}
                        >
                          {publishingId === item.id ? 'Publishing...' : 'Publish'}
                        </button>
                        <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0' }}>
                          This class is still a draft. Publish it so parents can see and register for it.
                        </p>
                      </div>
                    )}
                    <div className="class-actions">
                      <div className="registration-requests-card">
                        <div className="registration-requests-header">
                          <div>
                            <strong>Parent registration requests</strong>
                            <p>Approve or reject requests from parents who want to enroll a child in this class.</p>
                          </div>
                        </div>
                        {(enrollmentRequestsByClass[item.id] || []).filter((request) => request.status === 'pending').length === 0 ? (
                          <div className="empty-state compact">
                            <p>No pending requests yet.</p>
                          </div>
                        ) : (
                          <div className="registration-request-list">
                            {(enrollmentRequestsByClass[item.id] || []).filter((request) => request.status === 'pending').map((request) => (
                              <div key={request.id} className="registration-request-item">
                                <div>
                                  <strong>{request.student_name || 'Student'}</strong>
                                  <div className="registration-request-meta">Requested • {request.created_at ? new Date(request.created_at).toLocaleDateString('en-GB') : 'Recently added'}</div>
                                </div>
                                <div className="registration-request-actions">
                                  <button type="button" className="button-primary small" onClick={() => handleEnrollmentAction(item.id, request.id, 'approve')}>
                                    Approve
                                  </button>
                                  <button type="button" className="button-secondary small" onClick={() => handleEnrollmentAction(item.id, request.id, 'reject')}>
                                    Reject
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <form onSubmit={(e) => handleReminderSend(e, item.id, item.title)} className="reminder-form">
                        <label className="class-action-label">Send SMS payment reminder</label>
                        <p className="reminder-help-text">This sends a short SMS reminder to approved parents for this extra class payment.</p>
                      </form>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {activeSubTab === 'assignments' && (
        <div className="section-card">
          <div className="section-header">
            <div>
              <h3>{editingAssignmentId ? 'Edit assignment' : 'Create assignment'}</h3>
              <p>{editingAssignmentId ? 'Update this assignment — saving replaces its question list.' : 'Share new work for an extra-class session and collect student submissions.'}</p>
            </div>
          </div>
          <form onSubmit={handleAssignmentCreate} className="section-form">
            <select value={selectedClassId} onChange={(e) => setSelectedClassId(e.target.value)} required>
              <option value="">Select class</option>
              {classes.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
            </select>
            <input value={assignmentForm.title} onChange={(e) => setAssignmentForm({ ...assignmentForm, title: e.target.value })} placeholder="Assignment title" required />
            <textarea value={assignmentForm.description} onChange={(e) => setAssignmentForm({ ...assignmentForm, description: e.target.value })} placeholder="Description" />
            <textarea value={assignmentForm.instructions} onChange={(e) => setAssignmentForm({ ...assignmentForm, instructions: e.target.value })} placeholder="Instructions" />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <input value={assignmentForm.due_date} onChange={(e) => setAssignmentForm({ ...assignmentForm, due_date: e.target.value })} type="date" />
              <input value={assignmentForm.max_score} onChange={(e) => setAssignmentForm({ ...assignmentForm, max_score: e.target.value })} placeholder="Max score" type="number" />
            </div>

            <div className="question-builder-card">
              <div className="question-builder-head">
                <div>
                  <h4>Assignment questions</h4>
                  <p>Add questions manually or import them from CSV.</p>
                </div>
                <button type="button" className="button-ghost" onClick={() => setQuestionFile(null)}>Reset</button>
              </div>

              <div className="upload-section">
                <h4>Upload Questions from CSV</h4>
                <div className="csv-template">
                  <p className="small-text">CSV Format: question, type, options (pipe-separated), answer, points</p>
                  <p className="small-text">Types: essay, shortAnswer, multipleChoice, matching</p>
                  <button type="button" className="download-template-btn" onClick={handleDownloadCSVTemplate}>
                    <DownloadSimple size={18} /> Download Example Template
                  </button>
                </div>
                <div className="file-input-wrapper">
                  <input id="extra-class-csv-file-input" type="file" accept=".csv" onChange={handleCSVUpload} className="file-input" />
                  <label htmlFor="extra-class-csv-file-input" className="file-label">
                    <Upload size={18} /> Choose CSV File
                  </label>
                </div>
              </div>

              <div className="section-form">
                <input value={questionForm.question} onChange={(e) => setQuestionForm({ ...questionForm, question: e.target.value })} placeholder="Question prompt" />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <select value={questionForm.type} onChange={(e) => setQuestionForm({ ...questionForm, type: e.target.value })}>
                    <option value="essay">Essay</option>
                    <option value="shortAnswer">Short Answer</option>
                    <option value="multipleChoice">Multiple Choice</option>
                    <option value="matching">Matching</option>
                  </select>
                  <input value={questionForm.points} onChange={(e) => setQuestionForm({ ...questionForm, points: Number(e.target.value) })} placeholder="Points" type="number" />
                </div>
                <input value={questionForm.options} onChange={(e) => setQuestionForm({ ...questionForm, options: e.target.value })} placeholder="Options (pipe-separated for MCQ/matching)" />
                <input value={questionForm.answer} onChange={(e) => setQuestionForm({ ...questionForm, answer: e.target.value })} placeholder="Correct answer (optional)" />
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button type="button" className="button-primary" onClick={handleAddQuestion}>
                    <Plus size={16} /> Add question
                  </button>
                  <label className="button-secondary" style={{ cursor: 'pointer' }}>
                    <Upload size={16} />
                    <span>Upload CSV</span>
                    <input type="file" accept=".csv" onChange={handleCSVUpload} style={{ display: 'none' }} />
                  </label>
                </div>
              </div>

              {questions.length > 0 && (
                <div className="questions-preview">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <strong>Preview questions ({questions.length})</strong>
                    <span style={{ color: '#64748b', fontSize: 13 }}>{questionFile ? `Loaded from CSV: ${questionFile.name}` : 'Manual entries'}</span>
                  </div>
                  <div className="questions-list">
                    {questions.map((question, index) => (
                      <div key={`${question.question}-${index}`} className="question-item">
                        <div className="question-info">
                          <p className="question-text">{question.question}</p>
                          <span className="question-type">{question.type} • {question.points} point{question.points !== 1 ? 's' : ''}</span>
                        </div>
                        <button type="button" className="remove-btn" onClick={() => handleRemoveQuestion(index)}>
                          <Trash size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="button-primary" type="submit">{editingAssignmentId ? 'Update assignment' : 'Save assignment'}</button>
              {editingAssignmentId && (
                <button type="button" className="button-secondary" onClick={handleCancelEditAssignment}>Cancel edit</button>
              )}
            </div>
          </form>

          {selectedClassId && (
            <div style={{ marginTop: 24 }}>
              <h4 style={{ marginBottom: 8 }}>Existing assignments</h4>
              {(assignmentsByClass[selectedClassId] || []).length === 0 ? (
                <div className="empty-state compact"><p>No assignments yet for this class.</p></div>
              ) : (
                <div style={{ display: 'grid', gap: 8 }}>
                  {(assignmentsByClass[selectedClassId] || []).map((a) => (
                    <div key={a.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px' }}>
                      <div>
                        <strong>{a.title}</strong>
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                          {a.due_date ? `Due ${new Date(a.due_date).toLocaleDateString()}` : 'No due date'} · {a.max_score} pts · {a.questions_count} question{a.questions_count === 1 ? '' : 's'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" className="button-secondary small" onClick={() => handleEditAssignment(a)}>Edit</button>
                        <button type="button" className="button-secondary small" onClick={() => handleDeleteAssignment(a)}>Delete</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {assignmentSummary && (
            <div className="assignment-summary-card">
              <div className="assignment-summary-header">
                <div>
                  <p className="assignment-summary-label">Assignment summary</p>
                  <h4>{assignmentSummary.title}</h4>
                </div>
                <span className="assignment-summary-badge">Created</span>
              </div>
              <div className="assignment-summary-grid">
                <div>
                  <strong>Class</strong>
                  <span>{assignmentSummary.classTitle}</span>
                </div>
                <div>
                  <strong>Due date</strong>
                  <span>{assignmentSummary.dueDate}</span>
                </div>
                <div>
                  <strong>Questions</strong>
                  <span>{assignmentSummary.questionsCount}</span>
                </div>
                <div>
                  <strong>Max score</strong>
                  <span>{assignmentSummary.maxScore}</span>
                </div>
              </div>
              <p className="assignment-summary-description">{assignmentSummary.description}</p>
              <button type="button" className="button-secondary assignment-summary-action" onClick={() => handleViewSolvedStudents(assignmentSummary.classId || selectedClassId, assignmentSummary.id)}>
                View solved students
              </button>
            </div>
          )}
        </div>
      )}

      {activeSubTab === 'payouts' && (
        <div style={{ display: 'grid', gap: 20 }}>
          <div className="section-card">
            <div className="section-header">
              <div>
                <h3>Payout settlement details</h3>
                <p>Where your earnings from extra classes are sent. Submit once — a school admin verifies it, then every payment routes straight to this account automatically. No separate payout request needed after that.</p>
              </div>
            </div>

            {payoutDetails?.payout_verification_status === 'verified' ? (
              <div style={{ padding: 16, borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                <strong style={{ color: '#15803d' }}>Verified ✓</strong>
                <div style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>{payoutDetails.payout_account_name} — payments settle here automatically.</div>
              </div>
            ) : (
              <>
                {payoutDetails?.payout_verification_status === 'pending' && (
                  <div style={{ padding: 12, borderRadius: 8, background: '#fef3c7', color: '#92400e', fontSize: 13, marginBottom: 12 }}>
                    Submitted as <strong>{payoutDetails.payout_account_name}</strong> — waiting for a school admin to verify it.
                  </div>
                )}
                {payoutDetails?.payout_verification_status === 'rejected' && (
                  <div style={{ padding: 12, borderRadius: 8, background: '#fef2f2', color: '#991b1b', fontSize: 13, marginBottom: 12 }}>
                    Rejected: {payoutDetails.payout_rejection_reason || 'No reason given'} — please resubmit.
                  </div>
                )}
                <form onSubmit={handleSubmitPayoutDetails} className="section-form">
                  <select value={payoutForm.account_type} onChange={(e) => setPayoutForm({ ...payoutForm, account_type: e.target.value, bank_code: '' })}>
                    <option value="mobile_money">Mobile Money</option>
                    <option value="bank">Bank Account</option>
                  </select>
                  <select value={payoutForm.bank_code} onChange={(e) => setPayoutForm({ ...payoutForm, bank_code: e.target.value })} required>
                    <option value="">Select {payoutForm.account_type === 'bank' ? 'bank' : 'provider'}</option>
                    {banks.filter((b) => (payoutForm.account_type === 'bank' ? b.type !== 'mobile_money' : b.type === 'mobile_money')).map((b) => (
                      <option key={b.code} value={b.code}>{b.name}</option>
                    ))}
                  </select>
                  <input
                    value={payoutForm.account_number}
                    onChange={(e) => setPayoutForm({ ...payoutForm, account_number: e.target.value })}
                    placeholder={payoutForm.account_type === 'bank' ? 'Account number' : 'Mobile money number'}
                    required
                  />
                  <button className="button-secondary" type="submit" disabled={submittingPayoutDetails}>
                    {submittingPayoutDetails ? 'Verifying...' : 'Submit for Verification'}
                  </button>
                </form>
              </>
            )}
          </div>

          <div className="section-card">
            <div className="section-header">
              <div>
                <h3>Earnings</h3>
                <p>What's been collected for this class — each payment already settled straight to you.</p>
              </div>
            </div>
            <select value={selectedClassId} onChange={(e) => setSelectedClassId(e.target.value)} style={{ marginBottom: 12 }}>
              <option value="">Select class</option>
              {classes.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
            </select>
            <div style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f8fafc', marginBottom: 12 }}>
              <strong>Total earned: GHS {earnings.total_earned}</strong>
              <div style={{ fontSize: 12, color: '#64748b' }}>{earnings.cycle_count} paid billing cycle{earnings.cycle_count === 1 ? '' : 's'}</div>
            </div>
            {earnings.cycles.length > 0 && (
              <div style={{ display: 'grid', gap: 8 }}>
                {earnings.cycles.map((c) => (
                  <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, borderBottom: '1px solid #f1f5f9', padding: '6px 0' }}>
                    <span>{c.paid_at ? new Date(c.paid_at).toLocaleDateString('en-GB') : '—'}</span>
                    <span style={{ fontWeight: 600 }}>GHS {c.amount}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {activeSubTab === 'submissions' && (
        <div className="section-card">
          <div className="section-header">
            <div>
              <h3>Review student submissions</h3>
              <p>Open any submission to grade it with the same review flow used elsewhere in the portal.</p>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <select value={selectedClassId} onChange={(e) => handleSubmissionsClassChange(e.target.value)}>
              <option value="">Select class</option>
              {classes.map((item) => (
                <option key={item.id} value={item.id}>{item.title}</option>
              ))}
            </select>
            <select value={selectedAssignmentId} onChange={(e) => { setSelectedAssignmentId(e.target.value); loadSubmissions(selectedClassId, e.target.value); }}>
              <option value="">Select assignment</option>
              {(assignmentsByClass[selectedClassId] || []).map((assignment) => (
                <option key={assignment.id} value={assignment.id}>{assignment.title}</option>
              ))}
            </select>
          </div>
          {submissions.length === 0 ? <div className="empty-state"><p>No submissions yet.</p></div> : submissions.map((submission) => {
            const badge = formatStatusBadge(submission.status);
            return (
              <div key={submission.id} className="submission-card">
                <div className="submission-head">
                  <div>
                    <strong>{submission.student_name}</strong>
                    <div className="submission-meta">{submission.submission_date ? new Date(submission.submission_date).toLocaleString() : 'No timestamp'}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 999, color: badge.color, background: badge.background, fontSize: 12, fontWeight: 600 }}>
                      <ClipboardText size={14} /> {badge.text}
                    </span>
                    {submission.id ? (
                      <button onClick={() => openSubmissionReview(submission)} className="button-secondary">Review</button>
                    ) : (
                      <span style={{ color: '#6b7280', fontSize: 12 }}>No submission to review</span>
                    )}
                  </div>
                </div>
                <div className="submission-text">
                  {isJsonAnswers(submission.submission_text)
                    ? <em style={{ color: '#374151' }}>Answers submitted</em>
                    : (submission.submission_text || 'No text provided')}
                </div>
                {submission.grade ? (
                  <div className="submission-grade">
                    <div style={{ fontSize: 13, fontWeight: 700 }}>Grade</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, gap: 12, flexWrap: 'wrap' }}>
                      <span>{submission.grade.score} points</span>
                      <span>{submission.grade.feedback || 'No feedback'}</span>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {activeSubTab === 'sessions' && (
        <div style={{ display: 'grid', gap: 20 }}>
          <div className="section-card">
            <div className="section-header">
              <div>
                <h3>Log a session</h3>
                <p>Record that a class actually happened — parents see sessions-held and attendance stats built from this.</p>
              </div>
            </div>
            <select value={selectedClassId} onChange={(e) => setSelectedClassId(e.target.value)} style={{ marginBottom: 12 }} required>
              <option value="">Select class</option>
              {classes.map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
            </select>
            <form onSubmit={handleLogSession} className="section-form">
              <input
                value={sessionForm.session_date}
                onChange={(e) => setSessionForm({ ...sessionForm, session_date: e.target.value })}
                type="datetime-local"
                required
              />
              <input
                value={sessionForm.topic}
                onChange={(e) => setSessionForm({ ...sessionForm, topic: e.target.value })}
                placeholder="Topic covered (optional)"
              />
              <input
                value={sessionForm.attendance_count}
                onChange={(e) => setSessionForm({ ...sessionForm, attendance_count: e.target.value })}
                placeholder="Students present"
                type="number"
                min="0"
              />
              <textarea
                value={sessionForm.notes}
                onChange={(e) => setSessionForm({ ...sessionForm, notes: e.target.value })}
                placeholder="Notes (optional)"
              />
              <button className="button-primary" type="submit" disabled={submittingSession || !selectedClassId}>
                {submittingSession ? 'Logging...' : 'Log session'}
              </button>
            </form>
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
              <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 8px' }}>
                Nudge enrolled parents that a session is coming up (sent as an SMS right away — not scheduled).
              </p>
              <button
                type="button"
                className="button-secondary"
                onClick={handleSendSessionReminder}
                disabled={sendingSessionReminder || !selectedClassId}
              >
                {sendingSessionReminder ? 'Sending...' : 'Remind parents about next session'}
              </button>
            </div>
          </div>

          <div className="section-card">
            <div className="section-header">
              <div>
                <h3>Session history</h3>
                <p>Every session logged for the selected class.</p>
              </div>
            </div>
            {sessions.length === 0 ? (
              <div className="empty-state"><p>No sessions logged yet for this class.</p></div>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {sessions.map((s) => (
                  <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, borderBottom: '1px solid #f1f5f9', padding: '8px 0' }}>
                    <div>
                      <strong>{s.session_date ? new Date(s.session_date).toLocaleString() : 'No date'}</strong>
                      {s.topic && <div style={{ color: '#64748b' }}>{s.topic}</div>}
                      {s.notes && <div style={{ color: '#94a3b8', fontSize: 12 }}>{s.notes}</div>}
                    </div>
                    <span style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{s.attendance_count ?? 0} present</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {showGradingPanel && (
        <SubmissionGradingPanel
          assignmentId={selectedAssignmentId}
          submissions={submissions}
          initialSubmissionIdx={gradingSubmissionIndex}
          assignmentFetchUrl={`/api/extra-classes/${selectedClassId}/assignments/${selectedAssignmentId}`}
          gradePathResolver={(submission) => `/api/extra-classes/${selectedClassId}/assignments/${selectedAssignmentId}/submissions/${submission.id}/grade`}
          onClose={closeSubmissionReview}
          onGradingComplete={() => {
            loadSubmissions(selectedClassId, selectedAssignmentId);
          }}
        />
      )}

      <DialogModal
        isOpen={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        type={confirmDialog?.type || 'warning'}
        primaryAction={{ label: confirmDialog?.confirmLabel || 'Confirm', onClick: () => confirmDialog?.onConfirm() }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </div>
  );
}

export default TeacherExtraClassesSection;
